import classNames from 'classnames';
import { Link as ReactRouterLink, matchPath } from 'react-router-dom';

import * as SPA_PATHES from '@urls';
import { RouterLinkProps } from '@ui/Link';

import s from './Button.sss';

// constants
export const themes = {
  primary: s.primary,
  secondary: s.secondary,
  'danger-text': s.dangerText,
  text: s.text,
  swap: s.swap,
  noTheme: null,
};

// types
// Common props for all button types
export type CommonButtonProps = {
  theme?: keyof typeof themes,
  disabled?: boolean
};

// Specific types for different kinds of buttons:
// 1. Anchor Button with HTML `<a />` tag can be used as link on external resources
//    or internal resources with full page reload.
type AnchorButtonSpecificProps = Omit<React.HTMLProps<HTMLAnchorElement>, 'href'> & {
  href: string
};

// 2. Link button, same as Anchor Button, but using `<Link />` for react-router-dom
//    can be used as link on SPA routes.
type LinkButtonSpecificProps = RouterLinkProps;

// 3. Simple button with HTML `<button />` tag.
type HTMLButtonSpecificProps = React.DetailedHTMLProps<
React.ButtonHTMLAttributes<HTMLButtonElement>,
HTMLButtonElement
>;
type ButtonTypesSpecificProps =
  AnchorButtonSpecificProps
  | LinkButtonSpecificProps
  | HTMLButtonSpecificProps;

// Type that include all button types
export type MultiTypeButtonProps<
  TButtonType extends ButtonTypesSpecificProps = ButtonTypesSpecificProps,
> = CommonButtonProps & TButtonType;

// Types for every kind of button. Declared for reuse.
export type ButtonProps = MultiTypeButtonProps<HTMLButtonSpecificProps>;
export type AnchorButtonProps = MultiTypeButtonProps<AnchorButtonSpecificProps>;
export type LinkButtonProps = MultiTypeButtonProps<LinkButtonSpecificProps>;

export function Button(props: ButtonProps): React.ReactElement;
export function Button(props: AnchorButtonProps): React.ReactElement;
export function Button(props: LinkButtonProps): React.ReactElement;
export function Button(props: MultiTypeButtonProps): React.ReactElement {
  const {
    theme,
    disabled,
    className,
  } = props;

  const classes = classNames(
    s.button,
    themes[theme || 'primary'],
    { [s.disabled]: disabled },
    className,
  );

  if ('to' in props) {
    let pathname = '';
    let search: string | undefined;

    if (typeof props.to === 'string') {
      pathname = props.to;
    } else if ('pathname' in props.to && props.to.pathname) {
      pathname = props.to.pathname;
      search = props.to.search;
    }

    const match = matchPath(
      pathname,
      {
        path: Object.values(SPA_PATHES),
        exact: true,
      },
    );

    if ((typeof window === 'undefined' || window.IS_SPA_PAGE) && match) {
      return (
        <ReactRouterLink
          {...props}
          className={classes}
        />
      );
    }

    const href = search ? `${pathname}?${search}` : pathname;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { to, ...restProps } = props;

    return (
      <a
        {...restProps}
        className={classes}
        href={href}
      >
        {props.children}
      </a>
    );
  }

  if ('href' in props) {
    const { children, ...rest } = props;

    return <a {...rest} className={classes}>{children}</a>;
  }

  return <button type="button" {...props} className={classes} />;
}
